import { Platform } from 'react-native';

/**
 * Is this session a browser on an iPhone/iPad — i.e. someone who could be
 * handed to the App Store to claim natively?
 *
 * Three rules, each of which matters more than the sniffing itself:
 *
 * 1. FAIL OPEN. Return true only on a positive iOS match; anything else —
 *    including "couldn't tell" — falls through to the full web claim. A
 *    misfire on an iPhone costs us a download; a misfire on an Android
 *    strands someone who already handed over their data behind an App Store
 *    door they can't use (there is no Android build).
 *
 * 2. `navigator` is treated as absent by default. It doesn't exist on native
 *    Hermes or during the static-export prerender, but DOES in jsdom — the
 *    exact shape that broke cash-out at launch when crypto.randomUUID()
 *    passed every test and crashed on device. Gate on Platform.OS first,
 *    guard typeof, and call this INSIDE render — a module-level constant
 *    would be baked wrong at export time.
 *
 * 3. iPadOS 13+ reports itself as "Macintosh"; maxTouchPoints > 1 is the
 *    disambiguator. (An iPad falling through to the web claim would be
 *    harmless — this just routes it like the iPhone it effectively is.)
 */
/**
 * Android browser? Used only to swap the post-claim App Store pitch (an
 * Apple link Android can't use) for a go-home card — never for gating the
 * claim itself. Same defensive shape as isIosWeb: web-only, navigator
 * treated as absent by default.
 */
export function isAndroidWeb(): boolean {
  if (Platform.OS !== 'web') return false;
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent ?? '');
}

export function isIosWeb(): boolean {
  if (Platform.OS !== 'web') return false;
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent ?? '';
  if (/iPhone|iPad|iPod/i.test(ua)) return true;

  // iPadOS 13+ masquerades as desktop Safari.
  return /Macintosh/i.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
}
